
import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

const FormSection = () => {
  const { t } = useTranslation('partnership');
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    phone: '',
    email: '',
    website: '',
    experience: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const experienceOptions = t('form.experienceOptions', { returnObjects: true }) as string[];

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('companyName', formData.companyName);
      formBody.append('contactPerson', formData.contactPerson);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('website', formData.website);
      formBody.append('experience', formData.experience);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6e0oqqohb161tfd8ps0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ companyName: '', contactPerson: '', phone: '', email: '', website: '', experience: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="form" className="py-8 md:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-16">
          {/* Left info column */}
          <div className="order-2 lg:order-1">
            <span className="text-gold-600 font-semibold text-[10px] md:text-sm uppercase tracking-wider">
              {t('form.badge')}
            </span>
            <h2 className="text-lg md:text-3xl lg:text-4xl font-bold text-gray-900 mt-2 md:mt-3 mb-2 md:mb-6 font-serif">
              {t('form.title')}
            </h2>
            <p className="text-xs md:text-base text-gray-600 mb-4 md:mb-8 leading-relaxed">
              {t('form.description')}
            </p>

            <div className="space-y-3 md:space-y-6">
              <div className="flex items-start">
                <div className="w-8 h-8 md:w-12 md:h-12 flex items-center justify-center bg-gold-100 rounded-lg mr-2.5 md:mr-4 flex-shrink-0">
                  <i className="ri-phone-line text-sm md:text-xl text-gold-600"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-0.5 md:mb-1 text-xs md:text-base">{t('form.contact.hotlineLabel')}</h4>
                  <p className="text-gray-600 text-[10px] md:text-sm">+371 2572 6969 (Whatsapp / Call / Viber)</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-8 h-8 md:w-12 md:h-12 flex items-center justify-center bg-gold-100 rounded-lg mr-2.5 md:mr-4 flex-shrink-0">
                  <i className="ri-mail-line text-sm md:text-xl text-gold-600"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-0.5 md:mb-1 text-xs md:text-base">{t('form.contact.emailLabel')}</h4>
                  <p className="text-gray-600 text-[10px] md:text-sm">contact@thedaeg.com</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-8 h-8 md:w-12 md:h-12 flex items-center justify-center bg-gold-100 rounded-lg mr-2.5 md:mr-4 flex-shrink-0">
                  <i className="ri-map-pin-line text-sm md:text-xl text-gold-600"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-0.5 md:mb-1 text-xs md:text-base">{t('form.contact.addressLabel')}</h4>
                  <p className="text-gray-600 text-[10px] md:text-sm">Krisjana Barona Iela 96/98, Riga, Latvia</p>
                </div>
              </div>
            </div>

            <div className="mt-6 md:mt-10 bg-white rounded-xl md:rounded-2xl p-3 md:p-6 border border-gray-200 shadow-sm hidden md:block">
              <div className="flex items-center mb-2 md:mb-3">
                <i className="ri-double-quotes-l text-2xl md:text-3xl text-gold-400 mr-2 md:mr-3"></i>
                <span className="text-xs md:text-sm font-semibold text-gold-600">{t('form.commitment.badge')}</span>
              </div>
              <p className="text-gray-700 text-xs md:text-sm leading-relaxed italic">
                {t('form.commitment.quote')}
              </p>
            </div>
          </div>

          {/* Right form column */}
          <div className="bg-white rounded-xl md:rounded-2xl p-4 md:p-8 shadow-lg border border-gray-100 order-1 lg:order-2">
            <h3 className="text-base md:text-2xl font-bold text-gray-900 mb-3 md:mb-6 font-serif">{t('form.formTitle')}</h3>

            <form id="partnership-form" data-readdy-form onSubmit={handleSubmit} className="space-y-2.5 md:space-y-4">
              <div>
                <label htmlFor="companyName" className="block text-[10px] md:text-sm font-medium text-gray-700 mb-0.5 md:mb-1">
                  {t('form.labels.companyName')} <span className="text-red-500">{t('form.required')}</span>
                </label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  required
                  value={formData.companyName}
                  onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                  className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-xs md:text-sm"
                  placeholder={t('form.placeholders.companyName')}
                />
              </div>

              <div className="grid grid-cols-2 gap-2.5 md:gap-4">
                <div>
                  <label htmlFor="contactPerson" className="block text-[10px] md:text-sm font-medium text-gray-700 mb-0.5 md:mb-1">
                    {t('form.labels.contactPerson')} <span className="text-red-500">{t('form.required')}</span>
                  </label>
                  <input
                    type="text"
                    id="contactPerson"
                    name="contactPerson"
                    required
                    value={formData.contactPerson}
                    onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                    className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-xs md:text-sm"
                    placeholder={t('form.placeholders.contactPerson')}
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-[10px] md:text-sm font-medium text-gray-700 mb-0.5 md:mb-1">
                    {t('form.labels.phone')} <span className="text-red-500">{t('form.required')}</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-xs md:text-sm"
                    placeholder={t('form.placeholders.phone')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2.5 md:gap-4">
                <div>
                  <label htmlFor="partnerEmail" className="block text-[10px] md:text-sm font-medium text-gray-700 mb-0.5 md:mb-1">
                    {t('form.labels.email')} <span className="text-red-500">{t('form.required')}</span>
                  </label>
                  <input
                    type="email"
                    id="partnerEmail"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-xs md:text-sm"
                    placeholder={t('form.placeholders.email')}
                  />
                </div>
                <div>
                  <label htmlFor="website" className="block text-[10px] md:text-sm font-medium text-gray-700 mb-0.5 md:mb-1">
                    {t('form.labels.website')}
                  </label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-xs md:text-sm"
                    placeholder={t('form.placeholders.website')}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="experience" className="block text-[10px] md:text-sm font-medium text-gray-700 mb-0.5 md:mb-1">
                  {t('form.labels.experience')} <span className="text-red-500">{t('form.required')}</span>
                </label>
                <select
                  id="experience"
                  name="experience"
                  required
                  value={formData.experience}
                  onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                  className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-xs md:text-sm cursor-pointer"
                >
                  <option value="">{t('form.placeholders.experience')}</option>
                  {Array.isArray(experienceOptions) && experienceOptions.map((opt: string) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="partnerMessage" className="block text-[10px] md:text-sm font-medium text-gray-700 mb-0.5 md:mb-1">
                  {t('form.labels.message')}
                </label>
                <textarea
                  id="partnerMessage"
                  name="message"
                  rows={3}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-3 py-2 md:px-4 md:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent text-xs md:text-sm resize-none"
                  placeholder={t('form.placeholders.message')}
                ></textarea>
                <p className="text-[10px] md:text-xs text-gray-500 mt-0.5 md:mt-1">
                  {formData.message.length}/500 {t('form.charCount')}
                </p>
              </div>

              {submitStatus === 'success' && (
                <div className="p-2 md:p-3 bg-green-50 border border-green-200 rounded-lg text-[10px] md:text-sm text-green-700">
                  {t('form.success')}
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-2 md:p-3 bg-red-50 border border-red-200 rounded-lg text-[10px] md:text-sm text-red-700">
                  {t('form.error')}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || formData.message.length > 500}
                className="w-full px-4 py-2.5 md:px-6 md:py-3 bg-gold-600 text-white font-semibold rounded-lg hover:bg-gold-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-xs md:text-base"
              >
                {isSubmitting ? t('form.submitting') : t('form.submit')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FormSection;
